import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ContactImportCapacityPreview } from "@multi-chat/shared";
import { ChevronDown, LoaderCircle, TriangleAlert } from "lucide-react";
import { AppNotice } from "../../../components/common/AppNotice.js";
import { cn } from "../../../lib/utils.js";
import {
  classificationDisclosureLines,
  contactImportShortfall,
  contactsNotAddedCount,
  importOutcomeTone,
  placesLeftAfterImport,
  type ClassificationLineId,
} from "../import-outcome.js";

export function ImportContactsOutcomePanel({
  capacity,
  capacityLoading,
  autoLink,
  rowCount,
  detailsDefaultOpen = false,
}: {
  capacity: ContactImportCapacityPreview | null;
  capacityLoading: boolean;
  /** Auto-link value that produced `capacity`. Do not pass the in-flight toggle. */
  autoLink: boolean;
  rowCount: number;
  detailsDefaultOpen?: boolean;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [detailsOpen, setDetailsOpen] = useState(detailsDefaultOpen);
  const refreshing = capacityLoading && capacity != null;
  const tone = importOutcomeTone(capacity, capacityLoading && capacity == null);
  const format = (value: number) => value.toLocaleString(i18n.language);

  const lines =
    capacity?.classification != null && tone !== "pending" && tone !== "unmapped"
      ? classificationDisclosureLines(capacity.classification, autoLink)
      : [];
  const notAdded =
    capacity?.classification != null && tone !== "pending" && tone !== "unmapped"
      ? contactsNotAddedCount(capacity.classification, autoLink)
      : 0;
  const placesLeft = capacity ? placesLeftAfterImport(capacity) : null;
  const shortfall = capacity ? contactImportShortfall(capacity) : null;
  const canExplain = tone === "fits" || tone === "exact" || tone === "unlimited";
  const showDetails = canExplain && lines.length > 0;
  const showCapacityLine = placesLeft != null && tone !== "unlimited" && tone !== "exceeded";

  return (
    <section
      className="relative grid min-h-[4.5rem] gap-2 rounded-lg border border-line bg-surface py-3 ps-3 pe-8"
      data-testid="contact-import-outcome"
      aria-busy={refreshing || undefined}
      aria-label={t("contacts.import.capacity.headline")}
    >
      {refreshing ? (
        <p
          className="pointer-events-none absolute end-3 top-3 m-0"
          role="status"
          data-testid="contact-import-capacity-refreshing"
        >
          <LoaderCircle
            size={14}
            className="animate-spin text-muted motion-reduce:animate-none"
            aria-hidden="true"
          />
          <span className="sr-only">{t("contacts.import.capacity.classifying")}</span>
        </p>
      ) : null}
      {tone === "pending" ? (
        <p
          className="m-0 flex items-center gap-2 text-[0.8125rem] text-muted"
          data-testid="contact-import-capacity-pending"
          role="status"
          aria-live="polite"
        >
          <LoaderCircle
            size={14}
            className="animate-spin motion-reduce:animate-none"
            aria-hidden="true"
          />
          {t("contacts.import.capacity.classifying")}
        </p>
      ) : tone === "unmapped" ? (
        <p className="m-0 text-[0.8125rem] text-muted">{t("contacts.import.capacity.needsMapping")}</p>
      ) : tone === "exceeded" && capacity && shortfall != null && capacity.remainingCapacity != null ? (
        <div className="grid gap-2" data-testid="contact-import-capacity-exceeded">
          <p className="m-0 flex items-center gap-1.5 text-[0.8125rem] font-semibold text-foreground">
            <TriangleAlert size={15} className="shrink-0 text-warning" aria-hidden="true" />
            {t("contacts.import.capacity.exceededTitle")}
          </p>
          <dl className="m-0 grid gap-1.5">
            <BlockedFact
              label={t("contacts.import.capacity.needs")}
              value={format(capacity.contactsRequiredByImport)}
            />
            <BlockedFact
              label={t("contacts.import.capacity.availableNow")}
              value={format(capacity.remainingCapacity)}
            />
            <BlockedFact
              label={t("contacts.import.capacity.shortBy")}
              value={format(shortfall)}
            />
          </dl>
          <p className="m-0 text-[0.75rem] leading-snug text-muted">
            {t("contacts.import.capacity.exceededNext")}
          </p>
        </div>
      ) : (
        <>
          <div>
            <p
              className="m-0 text-[1.5rem] font-semibold tabular-nums tracking-tight text-foreground"
              data-testid="contact-import-outcome-count"
            >
              {capacity ? format(capacity.contactsRequiredByImport) : "—"}
            </p>
            <p className="m-0 mt-1 text-[0.8125rem] text-foreground">
              {t("contacts.import.capacity.headline")}
            </p>
          </div>
          <dl className="m-0 grid grid-cols-2 gap-x-3 border-t border-line/70 pt-2.5">
            <Fact label={t("contacts.import.capacity.rowsInFile")} value={format(rowCount)} />
            <Fact
              label={t("contacts.import.capacity.willNotAddLabel")}
              value={format(notAdded)}
            />
          </dl>
          {showCapacityLine && placesLeft != null ? (
            <p className="m-0 text-[0.75rem] text-muted" role="status">
              {tone === "exact"
                ? t("contacts.import.capacity.sentenceExact")
                : t("contacts.import.capacity.placesAfter", { count: format(placesLeft) })}
            </p>
          ) : null}
          {tone === "empty" ? (
            <div data-testid="contact-import-nothing-to-import">
              <AppNotice
                severity="info"
                title={t("contacts.import.capacity.emptyTitle")}
                description={t("contacts.import.capacity.emptyBody")}
              />
            </div>
          ) : null}
          {showDetails ? (
            <DetailsDisclosure
              open={detailsOpen}
              onToggle={() => setDetailsOpen((open) => !open)}
              lines={lines}
              format={format}
            />
          ) : null}
        </>
      )}
    </section>
  );
}

function DetailsDisclosure({
  open,
  onToggle,
  lines,
  format,
}: {
  open: boolean;
  onToggle: () => void;
  lines: { id: ClassificationLineId; count: number }[];
  format: (value: number) => string;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div>
      <button
        type="button"
        className="inline-flex min-h-8 cursor-pointer items-center gap-1 text-[0.8125rem] font-medium text-foreground"
        aria-expanded={open}
        aria-controls="contact-import-outcome-details"
        onClick={onToggle}
      >
        <ChevronDown
          size={14}
          aria-hidden="true"
          className={cn(
            "transition-transform motion-reduce:transition-none",
            open && "rotate-180",
          )}
        />
        {open ? t("contacts.import.capacity.hideWhy") : t("contacts.import.capacity.seeWhy")}
      </button>
      {open ? (
        <ul
          id="contact-import-outcome-details"
          className="m-0 mt-1 grid list-none gap-1 border-t border-line/70 p-0 pt-1.5 text-[0.8125rem]"
        >
          {lines.map((line) => (
            <li
              key={line.id}
              className="flex items-baseline justify-between gap-3 tabular-nums"
            >
              <span>{t(`contacts.import.capacity.breakdown.${line.id}`)}</span>
              <span className="font-medium">{format(line.count)}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function BlockedFact({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="m-0 text-[0.75rem] font-medium text-muted">{label}</dt>
      <dd className="m-0 text-[0.9375rem] font-semibold tabular-nums text-foreground">{value}</dd>
    </div>
  );
}

function Fact({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="m-0 text-[0.6875rem] font-medium text-muted">{label}</dt>
      <dd className="m-0 mt-0.5 truncate text-[0.875rem] font-medium tabular-nums text-foreground">
        {value}
      </dd>
    </div>
  );
}
